import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopNavbar, NAV_ICON_SEARCH_INDEX, NAV_ICON_FAVORITES_INDEX, NAV_ICON_SETTINGS_INDEX, NAV_ICON_LOCK_INDEX, NAV_ICON_HISTORY_INDEX } from '../../components/TopNavbar/TopNavbar';
import { ContentNav } from '../../components/ContentNav/ContentNav';
import { getRuntimeTmdbLanguage, getRuntimeTmdbRegion } from '../../api/tmdbAPI';
import { getMatchedList } from '../../api/backendAPI';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { useLockToggle } from '../../hooks/useLockToggle';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { useContentNavIds } from '../../hooks/useContentNavIds';
import { MovieDetailsEntry } from '../MovieDetails/MovieDetails';
import { SeriesDetailsEntry } from '../SeriesDetails/SeriesDetails';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import HubLoadingMessage from '../../components/HubLoadingMessage/HubLoadingMessage';
import './Kids.css';
import kidsMoviesImg from '../../assets/kids-movies.png';
import kidsSeriesImg from '../../assets/kids-series.png';

// ─── Types ─────────────────────────────────────────────────────────────────

interface KidsMovieItem {
  id: string;
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  vodStreamId?: string;
}

interface KidsSeriesItem {
  id: string;
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  seriesStreamId?: string;
}

// ─── Cache ──────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function cacheKey(username: string, language: string) {
  return `ott_kids_page_v1_${language}_${username}`;
}

interface KidsCache {
  movies: KidsMovieItem[];
  series: KidsSeriesItem[];
}

function loadCache(username: string, language: string): KidsCache | null {
  try {
    const raw = localStorage.getItem(cacheKey(username, language));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: KidsCache; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(username: string, language: string, data: KidsCache) {
  try {
    localStorage.setItem(cacheKey(username, language), JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface KidsProps {
  isActive?: boolean;
  onBack?: () => void;
  onNavigate?: (sectionId: string) => void;
  onOpenMovieDetails?: (entry: MovieDetailsEntry) => void;
  onOpenSeriesDetails?: (entry: SeriesDetailsEntry) => void;
  onOpenSettings?: () => void;
  onOpenSearch?: () => void;
  onOpenFavorites?: () => void;
  onOpenHistory?: () => void;
}

type FocusSection = 'topnav' | 'contentnav' | 'slider' | 'nav' | 'top-poster' | 'bottom';
const NAV_ICON_COUNT_K = 5;
type NavFocus = 'movies' | 'series';

// ─── Component ───────────────────────────────────────────────────────────────

export const Kids: React.FC<KidsProps> = ({
  isActive = true,
  onBack,
  onNavigate,
  onOpenMovieDetails,
  onOpenSeriesDetails,
  onOpenSettings,
  onOpenSearch,
  onOpenFavorites,
  onOpenHistory,
}) => {
  const { t, i18n } = useTranslation();
  const { authData } = useAuth();
  const { deviceId, activationPassword } = useDevice();
  const {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  } = useLockToggle();

  const [movies, setMovies] = useState<KidsMovieItem[]>([]);
  const [series, setSeries] = useState<KidsSeriesItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [sliderIdx, setSliderIdx] = useState(0);
  const [focusSection, setFocusSection] = useState<FocusSection>('slider');
  const [navFocus, setNavFocus] = useState<NavFocus>('movies');
  const [bottomIdx, setBottomIdx] = useState(0);
  const [topnavFocusedIdx, setTopnavFocusedIdx] = useState(0);
  const [contentnavFocusedId, setContentnavFocusedId] = useState('kids');
  const contentNavIds = useContentNavIds();

  const autoAdvRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Data fetching ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const username = authData?.user?.username ?? 'anon';
    const tmdbLanguage = getRuntimeTmdbLanguage(i18n.language);
    const tmdbRegion = getRuntimeTmdbRegion(i18n.language);

    const cached = loadCache(username, tmdbLanguage);
    if (cached) {
      setMovies(cached.movies);
      setSeries(cached.series);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        let movieItems: KidsMovieItem[] = [];
        let seriesItems: KidsSeriesItem[] = [];

        if (authData) {
          const [movResults, serResults] = await Promise.all([
            getMatchedList(deviceId, activationPassword, 'kids-movies', tmdbLanguage, tmdbRegion, 7, 'preview', consumeCatalogForceRefresh('kids-movies')),
            getMatchedList(deviceId, activationPassword, 'kids-series', tmdbLanguage, tmdbRegion, 7, 'preview', consumeCatalogForceRefresh('kids-series')),
          ]);
          if (cancelled) return;
          movieItems = movResults.map(m => ({
            id: String(m.tmdbId),
            title: m.title,
            posterUrl: m.posterUrl ?? undefined,
            backdropUrl: m.backdropUrl ?? undefined,
            vodStreamId: m.streamId,
          }));
          seriesItems = serResults.map(m => ({
            id: String(m.tmdbId),
            title: m.title,
            posterUrl: m.posterUrl ?? undefined,
            backdropUrl: m.backdropUrl ?? undefined,
            seriesStreamId: m.streamId,
          }));
        }

        if (!cancelled) {
          setMovies(movieItems);
          setSeries(seriesItems);
          setLoading(false);
          if (movieItems.length || seriesItems.length) saveCache(username, tmdbLanguage, { movies: movieItems, series: seriesItems });
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authData, deviceId, activationPassword, i18n.language]);

  // ─── Slider auto-advance ────────────────────────────────────────────────
  const sliderItems = movies.slice(0, 5);

  useEffect(() => {
    if (loading || !sliderItems.length) return;
    autoAdvRef.current = setInterval(
      () => setSliderIdx((prev) => (prev + 1) % sliderItems.length),
      5000,
    );
    return () => { if (autoAdvRef.current) clearInterval(autoAdvRef.current); };
  }, [loading, sliderItems.length]);

  const goToSlide = useCallback(
    (idx: number) => {
      if (autoAdvRef.current) { clearInterval(autoAdvRef.current); autoAdvRef.current = null; }
      setSliderIdx(idx);
      if (sliderItems.length) {
        autoAdvRef.current = setInterval(
          () => setSliderIdx((prev) => (prev + 1) % sliderItems.length),
          5000,
        );
      }
    },
    [sliderItems.length],
  );

  // ─── Open details helpers ───────────────────────────────────────────────
  const openMovie = useCallback(
    (item?: KidsMovieItem) => {
      if (!item) return;
      onOpenMovieDetails?.({
        tmdbId: Number(item.id),
        title: item.title,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        vodStreamId: item.vodStreamId,
      });
    },
    [onOpenMovieDetails],
  );

  const openSeries = useCallback(
    (item?: KidsSeriesItem) => {
      if (!item) return;
      onOpenSeriesDetails?.({
        tmdbId: Number(item.id),
        title: item.title,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        seriesStreamId: item.seriesStreamId,
      });
    },
    [onOpenSeriesDetails],
  );

  // ─── Keyboard navigation ────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          onBack?.();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.max(0, prev - 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx > 0) setContentnavFocusedId(contentNavIds[idx - 1]);
          } else if (focusSection === 'slider') {
            goToSlide((sliderIdx - 1 + (sliderItems.length || 1)) % (sliderItems.length || 1));
          } else if (focusSection === 'nav') {
            setFocusSection('top-poster');
          } else if (focusSection === 'top-poster') {
            setFocusSection('slider');
          } else if (focusSection === 'bottom') {
            setBottomIdx((prev) => Math.max(0, prev - 1));
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.min(NAV_ICON_COUNT_K - 1, prev + 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx < contentNavIds.length - 1) setContentnavFocusedId(contentNavIds[idx + 1]);
          } else if (focusSection === 'slider') {
            if (sliderIdx === sliderItems.length - 1) {
              setFocusSection('top-poster');
            } else {
              goToSlide(sliderIdx + 1);
            }
          } else if (focusSection === 'top-poster') {
            setFocusSection('nav');
            setNavFocus('movies');
          } else if (focusSection === 'bottom') {
            setBottomIdx((prev) => Math.min(3, prev + 1));
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (focusSection === 'contentnav') {
            setFocusSection('topnav');
          } else if (focusSection === 'slider') {
            setFocusSection('contentnav');
          } else if (focusSection === 'nav') {
            if (navFocus === 'series') {
              setNavFocus('movies');
            } else {
              setFocusSection('contentnav');
            }
          } else if (focusSection === 'top-poster') {
            setFocusSection('contentnav');
          } else if (focusSection === 'bottom') {
            if (bottomIdx <= 1) {
              setFocusSection('slider');
            } else if (bottomIdx === 2) {
              setFocusSection('top-poster');
            } else {
              setFocusSection('nav');
              setNavFocus('series');
            }
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setFocusSection('contentnav');
          } else if (focusSection === 'contentnav') {
            setFocusSection('slider');
          } else if (focusSection === 'slider') {
            setFocusSection('bottom');
            setBottomIdx(0);
          } else if (focusSection === 'nav') {
            if (navFocus === 'movies') {
              setNavFocus('series');
            } else {
              setFocusSection('bottom');
              setBottomIdx(3);
            }
          } else if (focusSection === 'top-poster') {
            setFocusSection('bottom');
            setBottomIdx(2);
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'topnav') {
            if (topnavFocusedIdx === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (topnavFocusedIdx === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (topnavFocusedIdx === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (topnavFocusedIdx === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (topnavFocusedIdx === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          } else if (focusSection === 'contentnav') {
            if (contentnavFocusedId === 'kids') {
              setFocusSection('slider');
            } else {
              onNavigate?.(contentnavFocusedId);
            }
          } else if (focusSection === 'slider') {
            openMovie(sliderItems[sliderIdx]);
          } else if (focusSection === 'nav') {
            onNavigate?.(navFocus === 'movies' ? 'kids-movies' : 'kids-series');
          } else if (focusSection === 'top-poster') {
            openMovie(movies[5]);
          } else if (focusSection === 'bottom') {
            if (bottomIdx <= 2) {
              openSeries(series[bottomIdx]);
            } else {
              openMovie(movies[6]);
            }
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isActive, focusSection, navFocus, bottomIdx, sliderIdx,
    sliderItems, movies, series, goToSlide, onBack, openMovie, openSeries, onNavigate,
    topnavFocusedIdx, contentnavFocusedId, contentNavIds, onOpenSearch, onOpenFavorites, onOpenSettings, onOpenHistory,
  ]);

  // ─── Derived data ────────────────────────────────────────────────────────
  const currentSlide = sliderItems[sliderIdx];
  const topPoster = movies[5];
  const bottomMovieItem = movies[6];
  const bottomSeriesItems = series.slice(0, 3);


  const sliderStyle: React.CSSProperties = currentSlide?.backdropUrl
    ? { backgroundImage: `url(${currentSlide.backdropUrl})` }
    : currentSlide?.posterUrl
    ? { backgroundImage: `url(${currentSlide.posterUrl})` }
    : {};

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <>
    <div className="kids">
      <TopNavbar
        focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
        onIconClick={(i) => {
          if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
          if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
          if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
          if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
          if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
        }}
      />
      <ContentNav
        activeId="kids"
        focusedId={focusSection === 'contentnav' ? contentnavFocusedId : undefined}
        onSelect={(id) => onNavigate?.(id)}
      />

      <div className="kids__main">
        {/* ── Top section ──────────────────────────────────── */}
        <div className="kids__top">
          {/* Slider */}
          <div
            className={[
              'kids__slider',
              loading ? 'kids__slider--loading' : '',
              focusSection === 'slider' ? 'kids__slider--focused' : '',
            ].filter(Boolean).join(' ')}
            style={sliderStyle}
            onClick={() => { setFocusSection('slider'); openMovie(currentSlide); }}
          >
            {!loading && currentSlide && (
              <>
                <div className="kids__slider-gradient" />
                <div className="kids__slider-title">{currentSlide.title}</div>
                <div className="kids__slider-dots">
                  {sliderItems.map((_, i) => (
                    <span
                      key={i}
                      className={`kids__slider-dot${i === sliderIdx ? ' kids__slider-dot--active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); goToSlide(i); }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right: top poster + nav cards */}
          <div className="kids__right">
            {/* Top portrait poster (movies[5]) */}
            <div
              className={[
                'kids__top-poster',
                loading || !topPoster ? 'kids__top-poster--loading' : '',
                focusSection === 'top-poster' ? 'kids__top-poster--focused' : '',
              ].filter(Boolean).join(' ')}
              style={topPoster?.posterUrl && !loading ? { backgroundImage: `url(${topPoster.posterUrl})` } : {}}
              onClick={() => { setFocusSection('top-poster'); openMovie(topPoster); }}
            >
              {topPoster?.title && !loading && (
                <div className="kids__poster-title">{topPoster.title}</div>
              )}
            </div>

            {/* Nav cards (Filmes / Séries) — SVG illustration */}
            <div className="kids__nav-cards">
              {/* Filmes nav card — Foguete + Planetas */}
              <div
                className={[
                  'kids__nav-card',
                  'kids__nav-card--movies',
                  focusSection === 'nav' && navFocus === 'movies' ? 'kids__nav-card--focused' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => { setFocusSection('nav'); setNavFocus('movies'); onNavigate?.('kids-movies'); }}
              >
                <img className="kids__nav-card-bg" src={kidsMoviesImg} alt="" />
                <span className="kids__nav-card-title">{t('kids.movies')}</span>
              </div>

              {/* Séries nav card — OVNi */}
              <div
                className={[
                  'kids__nav-card',
                  'kids__nav-card--series',
                  focusSection === 'nav' && navFocus === 'series' ? 'kids__nav-card--focused' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => { setFocusSection('nav'); setNavFocus('series'); onNavigate?.('kids-series'); }}
              >
                <img className="kids__nav-card-bg" src={kidsSeriesImg} alt="" />
                <span className="kids__nav-card-title">{t('kids.series')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom row (3 series + 1 movie, landscape) ──── */}
        <div className="kids__bottom">
          {bottomSeriesItems.map((item, i) => (
            <div
              key={item.id}
              className={[
                'kids__bottom-card',
                loading ? 'kids__bottom-card--loading' : '',
                focusSection === 'bottom' && bottomIdx === i ? 'kids__bottom-card--focused' : '',
              ].filter(Boolean).join(' ')}
              style={item.backdropUrl && !loading ? { backgroundImage: `url(${item.backdropUrl})` } : {}}
              onClick={() => { setFocusSection('bottom'); setBottomIdx(i); openSeries(item); }}
            >
              {item.title && !loading && (
                <div className="kids__bottom-title">{item.title}</div>
              )}
            </div>
          ))}

          {/* 4th bottom card: movie overflow */}
          <div
            className={[
              'kids__bottom-card',
              loading || !bottomMovieItem ? 'kids__bottom-card--loading' : '',
              focusSection === 'bottom' && bottomIdx === 3 ? 'kids__bottom-card--focused' : '',
            ].filter(Boolean).join(' ')}
            style={bottomMovieItem?.backdropUrl && !loading ? { backgroundImage: `url(${bottomMovieItem.backdropUrl})` } : {}}
            onClick={() => { setFocusSection('bottom'); setBottomIdx(3); openMovie(bottomMovieItem); }}
          >
            {bottomMovieItem?.title && !loading && (
              <div className="kids__bottom-title">{bottomMovieItem.title}</div>
            )}
          </div>
        </div>
      </div>
      <HubLoadingMessage active={loading} scope={`kids:${authData?.user?.username ?? 'anon'}`} />
    </div>
    {lockModalOpen && (
      <PasswordModal
        mode={lockModalMode}
        error={lockModalError ? t(lockModalError) : undefined}
        onConfirm={handleLockPasswordConfirm}
        onCancel={handleLockPasswordCancel}
      />
    )}
    </>
  );
};
